import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Archive,
  BadgeCheck,
  BarChart3,
  Boxes,
  ChevronRight,
  Clock3,
  CircleDollarSign,
  ExternalLink,
  Flame,
  Gift,
  Image,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  Pencil,
  Plus,
  Search,
  Settings,
  ShoppingBag,
  Tags,
  Trash2,
  Truck,
  X,
  XCircle,
} from "lucide-react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  actualizarOrden,
  actualizarProducto,
  actualizarPromocion,
  actualizarOfertaViral,
  auth,
  crearOfertaViral,
  crearProducto,
  crearPromocion,
  eliminarPromocion,
  eliminarOfertaViral,
  eliminarImagenPorPath,
  getOrdenes,
  getProductosAdmin,
  getPromociones,
  getOfertasVirales,
  subirImagenEn,
} from "../config/firebase";
import { CATEGORIAS } from "../config/categorias";
import { buildWhatsAppUrl } from "../config/store";
import { getRewardClaims, reviewRewardClaim, updateRewardSettings } from "../services/rewards";
import MonthlyDrawPanel from "../components/admin/MonthlyDrawPanel";
import ImageDropzone, { existingImages } from "../components/admin/ImageDropzone";
import "./Admin.css";
import "./AdminFixes.css";

const EMPTY_PRODUCT = {
  title: "",
  description: "",
  price: "",
  precioOriginal: "",
  categoria: "",
  stock: "",
  marca: "",
  etiquetas: "",
  disponible: true,
  destacado: false,
};

const EMPTY_PROMO = { title: "", image: "", link: "", badge: "Oferta" };
const EMPTY_VIRAL_OFFER = { titulo: "", descripcion: "", estado: "borrador", orden: 0 };

const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "productos", label: "Productos", icon: Package },
  { id: "categorias", label: "Categorías", icon: Tags },
  { id: "pedidos", label: "Pedidos", icon: ShoppingBag },
  { id: "contenido", label: "Contenido", icon: Image },
  { id: "ofertas-virales", label: "Ofertas virales", icon: Flame },
  { id: "premios", label: "Premios", icon: Gift },
  { id: "configuracion", label: "Configuración", icon: Settings, soon: true },
];

const ORDER_STATES = [
  ["pendiente", "Pendiente"],
  ["confirmado", "Confirmado"],
  ["procesado", "Procesado"],
  ["enviado", "Enviado"],
  ["entregado", "Entregado"],
  ["devuelto", "Devuelto"],
  ["cancelado", "Cancelado"],
];

const money = (value) =>
  new Intl.NumberFormat("es-EC", { style: "currency", currency: "USD" }).format(
    Number(value || 0),
  );

const date = (value) => {
  if (!value) return "Sin fecha";
  const parsed = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "Sin fecha"
    : new Intl.DateTimeFormat("es-EC", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(parsed);
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [active, setActive] = useState("dashboard");
  const [mobileNav, setMobileNav] = useState(false);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [promos, setPromos] = useState([]);
  const [viralOffers, setViralOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => onAuthStateChanged(auth, (current) => {
    setUser(current);
    setChecking(false);
  }), []);

  const loadAll = async () => {
    setLoading(true);
    const [productData, orderData, promoData, viralData] = await Promise.all([
      getProductosAdmin(),
      getOrdenes(),
      getPromociones(),
      getOfertasVirales(),
    ]);
    setProducts(productData);
    setOrders(orderData);
    setPromos(promoData);
    setViralOffers(viralData);
    setLoading(false);
  };

  useEffect(() => {
    if (user) loadAll();
  }, [user]);

  const flash = useCallback((text, type = "success") => {
    setNotice({ text, type });
    window.setTimeout(() => setNotice(null), 3500);
  }, []);

  const login = async (event) => {
    event.preventDefault();
    setLoginError("");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch {
      setLoginError("No pudimos iniciar sesión. Revisa el correo y la contraseña.");
    }
  };

  if (checking) return <AdminLoading />;
  if (!user) {
    return (
      <div className="admin-login">
        <a className="admin-login__back" href="/">← Regresar a la tienda</a>
        <div className="admin-login__brand">
          <span>ROMI</span> STORE
        </div>
        <form className="admin-login__card" onSubmit={login}>
          <div className="admin-login__eyebrow">Administración segura</div>
          <h1>Bienvenido de nuevo</h1>
          <p>Gestiona el catálogo, los pedidos y las campañas de la tienda.</p>
          <label>Correo electrónico</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <label>Contraseña</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          {loginError && <div className="admin-alert admin-alert--error">{loginError}</div>}
          <button type="submit">Ingresar al panel <ChevronRight size={18} /></button>
        </form>
      </div>
    );
  }

  const current = NAV_ITEMS.find((item) => item.id === active);

  return (
    <div className="admin-shell">
      <aside className={`admin-sidebar ${mobileNav ? "is-open" : ""}`}>
        <div className="admin-sidebar__brand"><span>ROMI</span> STORE <small>ADMIN</small></div>
        <button className="admin-sidebar__close" onClick={() => setMobileNav(false)}><X /></button>
        <nav>
          {NAV_ITEMS.map(({ id, label, icon: Icon, soon }) => (
            <button
              key={id}
              className={active === id ? "active" : ""}
              onClick={() => { setActive(id); setMobileNav(false); }}
            >
              <Icon size={19} /><span>{label}</span>{soon && <small>Próximo</small>}
            </button>
          ))}
        </nav>
        <div className="admin-sidebar__user">
          <div className="admin-avatar">{user.email?.[0]?.toUpperCase()}</div>
          <div><strong>Administrador</strong><span>{user.email}</span></div>
          <button title="Cerrar sesión" onClick={() => signOut(auth)}><LogOut size={18} /></button>
        </div>
      </aside>

      {mobileNav && <button className="admin-backdrop" onClick={() => setMobileNav(false)} aria-label="Cerrar menú" />}

      <main className="admin-main">
        <header className="admin-topbar">
          <button className="admin-menu" onClick={() => setMobileNav(true)}><Menu /></button>
          <div><span>ROMI STORE /</span><h1>{current?.label}</h1></div>
          <a href="/" target="_blank" rel="noreferrer">Ver tienda <ExternalLink size={16} /></a>
        </header>
        {notice && <div className={`admin-toast admin-toast--${notice.type}`}>{notice.text}</div>}
        <div className="admin-content">
          {loading ? <AdminLoading compact /> : (
            <>
              {active === "dashboard" && <Dashboard products={products} orders={orders} promos={promos} go={setActive} />}
              {active === "productos" && <Products products={products} reload={loadAll} flash={flash} />}
              {active === "categorias" && <Categories products={products} />}
              {active === "pedidos" && <Orders orders={orders} reload={loadAll} flash={flash} />}
              {active === "contenido" && <Content promos={promos} reload={loadAll} flash={flash} />}
              {active === "ofertas-virales" && <ViralOffersAdmin offers={viralOffers} reload={loadAll} flash={flash} />}
              {active === "premios" && <RewardsAdmin flash={flash} products={products} />}
              {active === "configuracion" && <ComingSoon title="Configuración" text="Datos de la tienda, WhatsApp, usuarios, permisos y preferencias operativas." />}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function AdminLoading({ compact = false }) {
  return <div className={compact ? "admin-loading compact" : "admin-loading"}><span /><p>Cargando panel...</p></div>;
}

function Dashboard({ products, orders, promos, go }) {
  const pending = orders.filter((o) => (o.estado || "pendiente") === "pendiente").length;
  const activeProducts = products.filter((p) => p.disponible !== false).length;
  const registered = orders.reduce((sum, o) => sum + Number(o.total || 0), 0);
  const cards = [
    ["Productos activos", activeProducts, Package, "violet"],
    ["Pedidos pendientes", pending, ShoppingBag, "orange"],
    ["Valor registrado", money(registered), CircleDollarSign, "green"],
    ["Campañas", promos.length, Image, "blue"],
  ];
  return <>
    <section className="admin-welcome"><div><span>Vista general</span><h2>Todo tu negocio en un solo lugar</h2><p>Información operacional de ROMI STORE. Los valores corresponden a pedidos registrados, no a ventas cobradas.</p></div><BarChart3 size={54} /></section>
    <div className="admin-stats">{cards.map(([label, value, Icon, color]) => <div className="admin-stat" key={label}><div className={`admin-stat__icon ${color}`}><Icon /></div><div><span>{label}</span><strong>{value}</strong></div></div>)}</div>
    <div className="admin-grid-2">
      <section className="admin-panel"><div className="admin-panel__head"><div><span>Actividad</span><h3>Pedidos recientes</h3></div><button onClick={() => go("pedidos")}>Ver todos</button></div><OrderMiniList orders={orders.slice(0, 5)} /></section>
      <section className="admin-panel"><div className="admin-panel__head"><div><span>Acciones rápidas</span><h3>¿Qué deseas gestionar?</h3></div></div><div className="quick-actions"><button onClick={() => go("productos")}><Plus /><span>Agregar producto<small>Publica un artículo en el catálogo</small></span></button><button onClick={() => go("pedidos")}><Truck /><span>Revisar pedidos<small>{pending} requieren atención</small></span></button><button onClick={() => go("contenido")}><Image /><span>Gestionar contenido<small>Campañas y banners</small></span></button></div></section>
    </div>
  </>;
}

function Products({ products, reload, flash }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("todos");
  const [editing, setEditing] = useState(null);
  const filtered = useMemo(() => products.filter((p) => {
    const name = String(p.title || p.nombre || "").toLowerCase();
    return name.includes(query.toLowerCase()) && (category === "todos" || p.categoria === category);
  }), [products, query, category]);
  const archive = async (p) => {
    await actualizarProducto(p.id, { disponible: p.disponible === false });
    flash(p.disponible === false ? "Producto activado" : "Producto archivado");
    reload();
  };
  return <>
    <div className="admin-page-head"><div><span>Catálogo</span><h2>Productos</h2><p>{products.length} productos registrados en Firestore.</p></div><button className="admin-primary" onClick={() => setEditing({})}><Plus size={18} /> Nuevo producto</button></div>
    <section className="admin-panel">
      <div className="admin-filters"><label><Search size={18} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar producto..." /></label><select value={category} onChange={(e) => setCategory(e.target.value)}>{CATEGORIAS.map((c) => <option value={c.id} key={c.id}>{c.nombre}</option>)}</select></div>
      <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Producto</th><th>Categoría</th><th>Precio</th><th>Stock</th><th>Estado</th><th /></tr></thead><tbody>{filtered.map((p) => <tr key={p.id}><td><div className="product-cell"><img src={p.imagenes?.[0] || p.imagen || "https://via.placeholder.com/56"} alt="" /><div><strong>{p.title || p.nombre}</strong><span>{p.marca || "Sin marca"}</span></div></div></td><td>{CATEGORIAS.find((c) => c.id === p.categoria)?.nombre || p.categoria}</td><td><strong>{money(p.price || p.precio)}</strong></td><td>{p.stock ?? 0}</td><td><span className={`status ${p.disponible === false ? "archivado" : "activo"}`}>{p.disponible === false ? "Archivado" : "Activo"}</span></td><td><div className="row-actions"><button title="Editar" onClick={() => setEditing(p)}><Pencil size={17} /></button><button title={p.disponible === false ? "Activar" : "Archivar"} onClick={() => archive(p)}><Archive size={17} /></button></div></td></tr>)}</tbody></table></div>
      {!filtered.length && <Empty icon={Boxes} title="No encontramos productos" text="Cambia los filtros o crea un producto nuevo." />}
    </section>
    {editing && <ProductModal product={editing} close={() => setEditing(null)} reload={reload} flash={flash} />}
  </>;
}

function ProductModal({ product, close, reload, flash }) {
  const [form, setForm] = useState({ ...EMPTY_PRODUCT, ...product, etiquetas: Array.isArray(product.etiquetas) ? product.etiquetas.join(", ") : product.etiquetas || "" });
  const [images, setImages] = useState(existingImages(product.imagenes || (product.imagen ? [product.imagen] : [])));
  const [saving, setSaving] = useState(false);
  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    const price = Number(form.price);
    const previous = form.precioOriginal ? Number(form.precioOriginal) : null;
    if (!Number.isFinite(price) || price <= 0) return flash("Ingresa un precio válido mayor a cero, sin el símbolo $.", "error");
    if (previous !== null && (!Number.isFinite(previous) || previous <= price)) return flash("El precio anterior debe ser mayor al precio actual.", "error");
    if (!images.length) return flash("Agrega al menos una imagen del producto.", "error");
    setSaving(true);
    try {
      const uploaded = [];
      for (const image of images) uploaded.push(image.file ? (await subirImagenEn(image.file, "productos")).url : image.url);
      const payload = { ...form, title: form.title.trim(), description: form.description.trim(), price, precioOriginal: previous, stock: Number(form.stock || 0), etiquetas: String(form.etiquetas || "").split(",").map((x) => x.trim()).filter(Boolean), imagenes: uploaded, imagen: uploaded[0] || "" };
      delete payload.id;
      if (product.id) await actualizarProducto(product.id, payload); else await crearProducto(payload);
      flash(product.id ? "Producto actualizado" : "Producto creado"); await reload(); close();
    } catch (err) { flash(err.message, "error"); } finally { setSaving(false); }
  };
  return <div className="admin-modal"><button className="admin-modal__backdrop" onClick={close} aria-label="Cerrar" /><form className="admin-modal__card" onSubmit={submit}><header><div><span>Catálogo</span><h2>{product.id ? "Editar producto" : "Nuevo producto"}</h2></div><button type="button" onClick={close}><X /></button></header><div className="admin-form-grid"><label className="wide">Nombre del producto<input name="title" value={form.title || ""} onChange={change} required /></label><label>Categoría<select name="categoria" value={form.categoria || ""} onChange={change} required><option value="">Seleccionar</option>{CATEGORIAS.filter((c) => c.id !== "todos").map((c) => <option value={c.id} key={c.id}>{c.nombre}</option>)}</select></label><label>Marca<input name="marca" value={form.marca || ""} onChange={change} /></label><label>Precio actual<input name="price" type="number" min="0.01" step="0.01" value={form.price || ""} onChange={change} placeholder="Ej. 29.99" required /><small>Solo números, sin el símbolo $.</small></label><label>Precio anterior (opcional)<input name="precioOriginal" type="number" min="0.01" step="0.01" value={form.precioOriginal || ""} onChange={change} placeholder="Ej. 39.99" /><small>Debe ser mayor al precio actual.</small></label><label>Stock<input name="stock" type="number" min="0" value={form.stock || ""} onChange={change} /></label><label>Etiquetas<input name="etiquetas" value={form.etiquetas || ""} onChange={change} placeholder="hogar, oferta" /></label><label className="wide">Descripción<textarea name="description" rows="5" value={form.description || ""} onChange={change} /></label><ImageDropzone items={images} onChange={setImages} maxFiles={8} label="Imágenes del producto" onError={(message) => flash(message, "error")} /><label className="check"><input type="checkbox" name="disponible" checked={form.disponible !== false} onChange={change} /> Producto activo</label><label className="check"><input type="checkbox" name="destacado" checked={Boolean(form.destacado)} onChange={change} /> Producto destacado</label></div><footer><button type="button" onClick={close}>Cancelar</button><button className="admin-primary" disabled={saving}>{saving ? "Guardando..." : "Guardar producto"}</button></footer></form></div>;
}

function Categories({ products }) {
  return <><div className="admin-page-head"><div><span>Organización</span><h2>Categorías</h2><p>Resumen del catálogo actual. La edición se habilitará después de migrarlas de código a Firestore.</p></div></div><div className="category-admin-grid">{CATEGORIAS.filter((c) => c.id !== "todos").map((c) => { const count = products.filter((p) => p.categoria === c.id).length; return <div className="category-admin-card" key={c.id}><div style={{ background: `${c.color}18`, color: c.color }}>{c.icono}</div><span><strong>{c.nombre}</strong><small>{count} producto{count === 1 ? "" : "s"}</small></span><ChevronRight size={18} /></div>; })}</div></>;
}

function Orders({ orders, reload, flash }) {
  const [filter, setFilter] = useState("todos");
  const shown = filter === "todos" ? orders : orders.filter((o) => (o.estado || "pendiente") === filter);
  const update = async (id, estado) => { await actualizarOrden(id, { estado }); flash("Estado del pedido actualizado"); reload(); };
  return <><div className="admin-page-head"><div><span>Operación</span><h2>Pedidos</h2><p>Pedidos registrados por la tienda antes de la confirmación manual por WhatsApp.</p></div></div><div className="order-tabs"><button className={filter === "todos" ? "active" : ""} onClick={() => setFilter("todos")}>Todos <b>{orders.length}</b></button>{ORDER_STATES.slice(0, 4).map(([id, label]) => <button className={filter === id ? "active" : ""} onClick={() => setFilter(id)} key={id}>{label} <b>{orders.filter((o) => (o.estado || "pendiente") === id).length}</b></button>)}</div><section className="admin-panel"><div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Pedido</th><th>Cliente</th><th>Fecha</th><th>Total</th><th>Estado</th><th>Contacto</th></tr></thead><tbody>{shown.map((o) => { const client = o.cliente || {}; const message = `Hola ${client.name || client.nombre || ""}, te contactamos de ROMI STORE por tu pedido #${o.id}.`; return <tr key={o.id}><td><strong>#{o.id.slice(0, 8).toUpperCase()}</strong><small className="table-sub">{o.productos?.length || 0} producto(s)</small></td><td><strong>{client.name || client.nombre} {client.surname || client.apellido}</strong><small className="table-sub">{client.phone || client.telefono || "Sin teléfono"}</small></td><td>{date(o.creadoEn)}</td><td><strong>{money(o.total)}</strong></td><td><select className={`status-select ${o.estado || "pendiente"}`} value={o.estado || "pendiente"} onChange={(e) => update(o.id, e.target.value)}>{ORDER_STATES.map(([id, label]) => <option value={id} key={id}>{label}</option>)}</select></td><td>{(client.phone || client.telefono) && <a className="wa-action" href={buildWhatsAppUrl(message)} target="_blank" rel="noreferrer">WhatsApp <ExternalLink size={14} /></a>}</td></tr>; })}</tbody></table></div>{!shown.length && <Empty icon={ShoppingBag} title="No hay pedidos en este estado" text="Los nuevos pedidos aparecerán aquí automáticamente." />}</section></>;
}

function OrderMiniList({ orders }) {
  if (!orders.length) return <Empty icon={ShoppingBag} title="Aún no hay pedidos" text="La actividad aparecerá aquí." />;
  return <div className="order-mini-list">{orders.map((o) => <div key={o.id}><div className="admin-avatar small">{(o.cliente?.name || o.cliente?.nombre || "P")[0]}</div><span><strong>{o.cliente?.name || o.cliente?.nombre || "Cliente"}</strong><small>#{o.id.slice(0, 7)} · {date(o.creadoEn)}</small></span><b>{money(o.total)}</b></div>)}</div>;
}

function Content({ promos, reload, flash }) {
  const [editing, setEditing] = useState(null);
  const remove = async (promo) => { if (!window.confirm(`¿Eliminar el banner “${promo.title || promo.titulo}”?`)) return; await eliminarPromocion(promo.id); flash("Banner eliminado"); reload(); };
  return <><div className="admin-page-head"><div><span>Marketing</span><h2>Contenido y campañas</h2><p>Estos banners alimentan la sección actual “Promociones del Día”. En el rediseño serán archivados o convertidos en campañas.</p></div><button className="admin-primary" onClick={() => setEditing({})}><Plus size={18} /> Nuevo banner</button></div><div className="content-grid">{promos.map((p) => <article className="content-card" key={p.id}><img src={p.image || p.imagen} alt={p.title || p.titulo} /><div><span>{p.badge || p.etiqueta || "Oferta"}</span><h3>{p.title || p.titulo}</h3><small>{p.link || p.enlace || "Sin enlace"}</small><footer><button onClick={() => setEditing(p)}><Pencil size={16} /> Editar</button><button className="danger" onClick={() => remove(p)}><Trash2 size={16} /> Eliminar</button></footer></div></article>)}</div>{!promos.length && <section className="admin-panel"><Empty icon={Image} title="No hay contenido publicado" text="Crea una campaña cuando la nueva portada esté lista." /></section>}{editing && <PromoModal promo={editing} close={() => setEditing(null)} reload={reload} flash={flash} />}</>;
}

function PromoModal({ promo, close, reload, flash }) {
  const [form, setForm] = useState({ ...EMPTY_PROMO, ...promo }); const [saving, setSaving] = useState(false);
  const submit = async (e) => { e.preventDefault(); setSaving(true); try { const data = { title: form.title, image: form.image, link: form.link, badge: form.badge }; if (promo.id) await actualizarPromocion(promo.id, data); else await crearPromocion(data); flash(promo.id ? "Banner actualizado" : "Banner creado"); await reload(); close(); } catch (err) { flash(err.message, "error"); } finally { setSaving(false); } };
  return <div className="admin-modal"><button className="admin-modal__backdrop" onClick={close} aria-label="Cerrar" /><form className="admin-modal__card small" onSubmit={submit}><header><div><span>Contenido</span><h2>{promo.id ? "Editar banner" : "Nuevo banner"}</h2></div><button type="button" onClick={close}><X /></button></header><div className="admin-form-grid"><label className="wide">Título<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></label><label className="wide">URL de imagen<input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} required /></label><label className="wide">Enlace<input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} /></label><label className="wide">Etiqueta<input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} /></label></div><footer><button type="button" onClick={close}>Cancelar</button><button className="admin-primary" disabled={saving}>{saving ? "Guardando..." : "Guardar banner"}</button></footer></form></div>;
}

function ViralOffersAdmin({ offers, reload, flash }) {
  const [editing, setEditing] = useState(null);
  const remove = async (offer) => {
    if (!window.confirm(`¿Eliminar definitivamente la oferta “${offer.titulo}”? También se eliminarán sus imágenes.`)) return;
    try {
      await eliminarOfertaViral(offer.id);
      await Promise.allSettled([offer.imagenPrincipal?.path, offer.imagenOferta?.path].filter(Boolean).map(eliminarImagenPorPath));
      flash("Oferta viral eliminada");
      reload();
    } catch (error) { flash(error.message, "error"); }
  };
  const toggle = async (offer) => {
    try {
      await actualizarOfertaViral(offer.id, { estado: offer.estado === "publicado" ? "borrador" : "publicado" });
      flash(offer.estado === "publicado" ? "Oferta ocultada" : "Oferta publicada");
      reload();
    } catch (error) { flash(error.message, "error"); }
  };
  return <><div className="admin-page-head"><div><span>Campañas rápidas</span><h2>Ofertas virales</h2><p>Vitrina de promociones temporales para productos vistos en TikTok. No modifica el catálogo.</p></div><button className="admin-primary" onClick={() => setEditing({})}><Plus size={18} /> Nueva oferta viral</button></div>
    <div className="viral-admin-grid">{offers.map((offer) => <article className="viral-admin-card" key={offer.id}><div className="viral-admin-card__images"><img src={offer.imagenPrincipal?.url} alt="" /><img src={offer.imagenOferta?.url} alt="" /></div><div><span className={`status ${offer.estado === "publicado" ? "activo" : "archivado"}`}>{offer.estado === "publicado" ? "Publicada" : "Oculta"}</span><small>Orden {Number(offer.orden || 0)}</small><h3>{offer.titulo}</h3><p>{offer.descripcion}</p><footer><button onClick={() => setEditing(offer)}><Pencil />Editar</button><button onClick={() => toggle(offer)}>{offer.estado === "publicado" ? <Archive /> : <BadgeCheck />}{offer.estado === "publicado" ? "Ocultar" : "Publicar"}</button><button className="danger" onClick={() => remove(offer)}><Trash2 />Eliminar</button></footer></div></article>)}</div>
    {!offers.length && <section className="admin-panel"><Empty icon={Flame} title="Todavía no hay ofertas virales" text="Crea la primera campaña con una imagen principal y otra de la promoción." /></section>}
    {editing && <ViralOfferModal offer={editing} close={() => setEditing(null)} reload={reload} flash={flash} />}
  </>;
}

function ViralOfferModal({ offer, close, reload, flash }) {
  const [form, setForm] = useState({ ...EMPTY_VIRAL_OFFER, ...offer });
  const [images, setImages] = useState(existingImages([offer.imagenPrincipal, offer.imagenOferta].filter(Boolean)));
  const [saving, setSaving] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    if (images.length !== 2) return flash("Agrega exactamente dos imágenes: principal y oferta.", "error");
    setSaving(true);
    try {
      const uploaded = [];
      for (const image of images) uploaded.push(image.file ? await subirImagenEn(image.file, "ofertas-virales/campanas") : { url: image.url, path: image.path || "" });
      const data = { titulo: form.titulo.trim(), descripcion: form.descripcion.trim(), estado: form.estado, orden: Number(form.orden || 0), imagenPrincipal: uploaded[0], imagenOferta: uploaded[1] };
      if (offer.id) {
        await actualizarOfertaViral(offer.id, data);
        const retained = new Set(uploaded.map((image) => image.path).filter(Boolean));
        const removed = [offer.imagenPrincipal?.path, offer.imagenOferta?.path].filter((path) => path && !retained.has(path));
        await Promise.allSettled(removed.map(eliminarImagenPorPath));
      } else await crearOfertaViral(data);
      flash(offer.id ? "Oferta viral actualizada" : "Oferta viral creada");
      await reload();
      close();
    } catch (error) { flash(error.message, "error"); }
    finally { setSaving(false); }
  };
  return <div className="admin-modal"><button className="admin-modal__backdrop" onClick={close} aria-label="Cerrar" /><form className="admin-modal__card" onSubmit={submit}><header><div><span>Ofertas virales</span><h2>{offer.id ? "Editar oferta" : "Nueva oferta"}</h2></div><button type="button" onClick={close}><X /></button></header><div className="admin-form-grid"><label className="wide">Nombre de la promoción<input value={form.titulo} onChange={(event) => setForm({ ...form, titulo: event.target.value })} placeholder="Ej. Spa en casa" required /></label><label className="wide">Descripción corta<textarea rows="4" value={form.descripcion} onChange={(event) => setForm({ ...form, descripcion: event.target.value })} placeholder="Explica el producto y la promoción que verá el cliente." required /></label><label>Visibilidad<select value={form.estado} onChange={(event) => setForm({ ...form, estado: event.target.value })}><option value="borrador">Oculta · borrador</option><option value="publicado">Publicada en la tienda</option></select></label><label>Orden de aparición<input type="number" min="0" value={form.orden} onChange={(event) => setForm({ ...form, orden: event.target.value })} /></label><div className="wide viral-image-instructions"><strong>Dos imágenes obligatorias</strong><span>1. Imagen principal o hero · 2. Imagen con la oferta y sus opciones. Puedes arrastrarlas para cambiar el orden.</span></div><div className="wide"><ImageDropzone items={images} onChange={setImages} maxFiles={2} label="Imágenes de la oferta viral" onError={(message) => flash(message, "error")} /></div></div><footer><button type="button" onClick={close}>Cancelar</button><button className="admin-primary" disabled={saving}>{saving ? "Guardando..." : offer.id ? "Actualizar oferta" : "Crear oferta"}</button></footer></form></div>;
}

function ComingSoon({ title, text }) { return <section className="coming-soon"><div><Gift size={36} /></div><span>Próxima etapa</span><h2>{title}</h2><p>{text}</p></section>; }
function Empty({ icon: Icon, title, text }) { return <div className="admin-empty"><Icon /><strong>{title}</strong><span>{text}</span></div>; }

function RewardsAdmin({ flash, products }) {
  const [claims, setClaims] = useState([]);
  const [status, setStatus] = useState("pendiente");
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [settings, setSettings] = useState({
    period: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
    limite8: 2,
    limite10: 2,
  });
  const load = useCallback(async () => {
    setLoading(true);
    try { setClaims(await getRewardClaims()); }
    catch (err) { flash("No fue posible cargar las solicitudes: " + err.message, "error"); }
    finally { setLoading(false); }
  }, [flash]);
  useEffect(() => { load(); }, [load]);
  const review = async (claim, next) => {
    let motivo = "";
    if (next === "rechazado") {
      motivo = window.prompt("Motivo del rechazo para el cliente:", "La guía todavía no consta como entregada o retirada.");
      if (motivo === null) return;
    }
    try { await reviewRewardClaim({ id: claim.id, estado: next, motivo }); flash(`Solicitud ${next}`); load(); }
    catch (err) { flash(err.message, "error"); }
  };
  const saveLimits = async (event) => {
    event.preventDefault();
    try { await updateRewardSettings({ ...settings, limite8: Number(settings.limite8), limite10: Number(settings.limite10) }); flash("Cupos mensuales actualizados"); }
    catch (err) { flash(err.message, "error"); }
  };
  const shown = status === "todos" ? claims : claims.filter((c) => c.estado === status);
  const counts = ["pendiente", "aprobado", "rechazado"].reduce((acc, key) => ({ ...acc, [key]: claims.filter((c) => c.estado === key).length }), {});
  return <>
    <div className="admin-page-head"><div><span>Fidelización post-entrega</span><h2>Caja Misteriosa</h2><p>Valida manualmente las guías y controla los premios limitados del mes.</p></div><a className="admin-primary" href="/premios" target="_blank" rel="noreferrer">Ver experiencia <ExternalLink size={16} /></a></div>
    <div className="admin-stats reward-admin-stats"><div className="admin-stat"><div className="admin-stat__icon orange"><Clock3 /></div><div><span>Pendientes</span><strong>{counts.pendiente || 0}</strong></div></div><div className="admin-stat"><div className="admin-stat__icon green"><BadgeCheck /></div><div><span>Aprobadas</span><strong>{counts.aprobado || 0}</strong></div></div><div className="admin-stat"><div className="admin-stat__icon violet"><Gift /></div><div><span>Premios abiertos</span><strong>{claims.filter((c) => c.premio).length}</strong></div></div><div className="admin-stat"><div className="admin-stat__icon blue"><XCircle /></div><div><span>Rechazadas</span><strong>{counts.rechazado || 0}</strong></div></div></div>
    <div className="admin-grid-2 rewards-admin-grid"><section className="admin-panel"><div className="admin-panel__head"><div><span>Validación manual</span><h3>Solicitudes</h3></div></div><div className="order-tabs reward-tabs"><button className={status === "todos" ? "active" : ""} onClick={() => setStatus("todos")}>Todas <b>{claims.length}</b></button>{["pendiente", "aprobado", "rechazado"].map((key) => <button className={status === key ? "active" : ""} onClick={() => setStatus(key)} key={key}>{key[0].toUpperCase() + key.slice(1)} <b>{counts[key] || 0}</b></button>)}</div>{loading ? <AdminLoading compact /> : <div className="reward-claim-list">{shown.map((claim) => <article key={claim.id}><div className="reward-claim-list__main"><span className={`status ${claim.estado}`}>{claim.estado}</span><h4>{claim.nombre}</h4><p>{claim.telefono} · Guía <strong>{claim.guia}</strong></p><small>{date(claim.fecha)}</small></div>{claim.premio ? <div className="reward-won"><small>Premio entregado</small><strong>${claim.premio} OFF</strong><span>{claim.codigo}</span></div> : claim.estado === "pendiente" ? <div className="reward-actions"><button onClick={() => review(claim, "aprobado")}><BadgeCheck />Aprobar</button><button className="danger" onClick={() => review(claim, "rechazado")}><XCircle />Rechazar</button></div> : <div className="reward-actions"><button onClick={() => review(claim, "aprobado")}><BadgeCheck />Aprobar</button></div>}</article>)}</div>}{!loading && !shown.length && <Empty icon={Gift} title="No hay solicitudes" text="Las solicitudes de clientes aparecerán aquí." />}</section><section className="admin-panel reward-settings"><div className="admin-panel__head"><div><span>Inventario mensual</span><h3>Premios limitados</h3></div></div><form onSubmit={saveLimits}><label>Periodo<input type="month" value={settings.period} onChange={(e) => setSettings({ ...settings, period: e.target.value })} required /></label><div><label>Premios de $8<input type="number" min="0" value={settings.limite8} onChange={(e) => setSettings({ ...settings, limite8: e.target.value })} /></label><label>Premios de $10<input type="number" min="0" value={settings.limite10} onChange={(e) => setSettings({ ...settings, limite10: e.target.value })} /></label></div><p>Cuando estos cupos se agoten, el sistema asignará únicamente premios de $3 y $5.</p><button className="admin-primary">Guardar configuración</button></form></section></div>
    <MonthlyDrawPanel products={products} flash={flash} />
  </>;
}
